/**
 * Approval Inbox view — F12 authority gate.
 *
 * From V1 scaffold lineage: pending approvals for gated actions
 * (delivery, deploy, DNS, payment, external send).
 * Each approval shows the tool call, authority context, and approve/reject.
 * F13A HELD appears here as "Delivery: Awaiting Authorization."
 */

import { useState, useEffect, useCallback } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";
import { getJWT } from "../lib/portal-session";

interface Approval {
  approval_id: string;
  toolName: string;
  args: Record<string, any>;
  createdAt: string;
}

interface InboxViewProps {
  isOperator: boolean;
  onPendingCount?: (count: number) => void;
}

export function InboxView({ isOperator, onPendingCount }: InboxViewProps) {
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, any>>({});

  const membraneBypass = isOperator;

  const loadApprovals = useCallback(async () => {
    const clients = getClients();
    if (!clients) { setLoading(false); return; }
    try {
      // Use the control client's MCP tool call (V1 scaffold pattern)
      const result = await clients.control.mcpToolCall("approvals.list", {});
      const pending = (result?.result || result || []) as Approval[];
      setApprovals(pending);
      onPendingCount?.(pending.length);
    } catch {
      // MCP may not be available — try the pending endpoint with proper auth
      try {
        const token = getJWT();
        const res = await fetch("/mcp/pending", {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        const body = await res.json();
        const pending = body.ok ? (body.result || []) : [];
        setApprovals(pending);
        onPendingCount?.(pending.length);
      } catch (e: any) {
        setApprovals([]);
        onPendingCount?.(0);
      }
    } finally {
      setLoading(false);
    }
  }, [onPendingCount]);

  useEffect(() => { loadApprovals(); }, [loadApprovals]);

  async function approve(approvalId: string) {
    setProcessing(approvalId);
    const clients = getClients();
    if (!clients) { setProcessing(null); return; }
    try {
      // Use MCP tool call first
      let result: any;
      try {
        result = await clients.control.mcpToolCall("approvals.approve", { approval_id: approvalId });
      } catch {
        // Fallback to direct endpoint with proper auth
        const token = getJWT();
        const res = await fetch(`/mcp/approve/${approvalId}`, {
          method: "POST",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        const body = await res.json();
        if (!body.ok) throw new Error(body.code || body.error || "Approval failed");
        result = body.result;
      }
      setResults((prev) => ({ ...prev, [approvalId]: result }));
      setApprovals((prev) => prev.filter((a) => a.approval_id !== approvalId));
      onPendingCount?.(approvals.length - 1);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setProcessing(null);
    }
  }

  async function reject(approvalId: string) {
    setProcessing(approvalId);
    const clients = getClients();
    if (!clients) { setProcessing(null); return; }
    try {
      try {
        await clients.control.mcpToolCall("approvals.reject", { approval_id: approvalId });
      } catch {
        const token = getJWT();
        await fetch(`/mcp/approve/${approvalId}`, {
          method: "DELETE",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
          credentials: "include",
        });
      }
      setApprovals((prev) => prev.filter((a) => a.approval_id !== approvalId));
      onPendingCount?.(approvals.length - 1);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setProcessing(null);
    }
  }

  if (loading) return <div className="loading">{applyMembrane("Loading approvals…", membraneBypass)}</div>;

  return (
    <div className="view">
      <div className="view-header">
        <span className="eyebrow">{applyMembrane("Authority", membraneBypass)}</span>
        <h1>{applyMembrane("Approval Inbox", membraneBypass)}</h1>
        <button className="btn-secondary" onClick={loadApprovals}>Refresh</button>
      </div>

      {error && <div className="error-state">{error}</div>}

      {approvals.length === 0 ? (
        <div className="empty-state">
          {applyMembrane(
            "No pending approvals. Gated actions — delivery, deploy, DNS, payment — appear here for your authorization.",
            membraneBypass
          )}
        </div>
      ) : (
        <div className="approval-list">
          {approvals.map((ap) => {
            const result = results[ap.approval_id];
            return (
              <div key={ap.approval_id} className="card approval-card">
                <div className="card-title">{applyMembrane(ap.toolName, membraneBypass)}</div>
                <div className="approval-meta">
                  <span className="badge" data-status="pending">
                    {applyMembrane("Awaiting Authorization", membraneBypass)}
                  </span>
                  <span className="muted timestamp">{ap.createdAt || "—"}</span>
                </div>
                <pre className="approval-args">
                  {JSON.stringify(ap.args, null, 2)}
                </pre>
                {result ? (
                  <div className="approval-result">
                    <pre>{JSON.stringify(result, null, 2)}</pre>
                  </div>
                ) : (
                  <div className="approval-actions">
                    <button
                      className="btn-primary approve-btn"
                      onClick={() => approve(ap.approval_id)}
                      disabled={processing === ap.approval_id}
                    >
                      {processing === ap.approval_id ? "Processing…" : "Approve"}
                    </button>
                    <button
                      className="btn-secondary reject-btn"
                      onClick={() => reject(ap.approval_id)}
                      disabled={processing === ap.approval_id}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
